import React from "react";
import { Key, Inbox, Wallet, Search, MousePointerClick, Car, ArrowRight } from "lucide-react";

const HOST_STEPS = [
  {
    icon: Key,
    title: "List your car in minutes",
    body: "Snap photos, set your price, choose your availability — we handle verification, KYC and the legal layer.",
  },
  {
    icon: Inbox,
    title: "Get verified booking requests",
    body: "Only screened, ID-verified renters. Approve trips with one tap and keep full control of your calendar.",
  },
  {
    icon: Wallet,
    title: "Earn passive income every month",
    body: "Your car works for you while it sits idle. Payouts land straight in your UAE bank, fines and tolls auto-deducted.",
  },
];

const RENTER_STEPS = [
  {
    icon: Search,
    title: "Search available cars in Dubai",
    body: "Filter by neighbourhood, make, model and trip length. Real photos, real reviews, real-time availability.",
  },
  {
    icon: MousePointerClick,
    title: "Book instantly with full transparency",
    body: "Frozen-hold deposit, live countdown, transparent fees. You see every line item before you drive.",
  },
  {
    icon: Car,
    title: "Drive with complete confidence",
    body: "360° check-in protects you. Live fine tracking keeps surprises out. Roadside backup is a tap away.",
  },
];

const SUPPORT_IMAGE =
  "https://images.unsplash.com/photo-1432462770865-65b70566d673?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBjYXIlMjBrZXlzJTIwc3RlZXJpbmclMjB3aGVlbHxlbnwwfHx8fDE3ODIzMjk3MDR8MA&ixlib=rb-4.1.0&q=85";

export default function HowItWorks() {
  return (
    <section
      id="how"
      data-testid="how-it-works-section"
      className="relative py-24 sm:py-32 lg:py-40 bg-white border-t border-carvny-border"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Editorial header */}
        <div className="grid grid-cols-12 gap-y-8 lg:gap-x-12">
          <div className="col-span-12 lg:col-span-4">
            <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-carvny-red">
              §03 — How it works
            </div>
            <div className="rule mt-4" />
          </div>
          <div className="col-span-12 lg:col-span-8">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[64px] font-black tracking-tightest text-carvny-ink leading-[1.02]">
              Two sides.
              <span className="font-serif-italic font-normal text-carvny-ink/85"> One </span>
              <span className="text-carvny-red">playbook.</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-carvny-muted leading-relaxed max-w-2xl">
              Whether you&apos;re putting your car to work or borrowing one for
              the weekend, the path is short and the rules are the same for
              everyone.
            </p>
          </div>
        </div>

        {/* Two columns */}
        <div className="mt-16 sm:mt-20 grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
          {/* Hosts column — premium dark */}
          <div
            data-testid="how-hosts-column"
            className="relative bg-carvny-ink text-white rounded-[28px] p-8 sm:p-12 overflow-hidden"
          >
            <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-carvny-red/40 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-carvny-red">
                  For Hosts
                </span>
                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">
                  Passive income
                </span>
              </div>
              <div className="rule mt-5 opacity-30" />

              <h3 className="font-display mt-7 text-[34px] sm:text-[44px] font-black tracking-tight leading-[1.02]">
                Turn idle hours into
                <span className="font-serif-italic font-normal text-white/85"> rent cheques.</span>
              </h3>

              <ol className="mt-12 space-y-7">
                {HOST_STEPS.map((step, i) => {
                  const Icon = step.icon;
                  return (
                    <li
                      key={step.title}
                      data-testid={`how-host-step-${i + 1}`}
                      className="flex items-start gap-5"
                    >
                      <div className="shrink-0 w-12 h-12 rounded-2xl bg-white text-carvny-red flex items-center justify-center">
                        <Icon size={22} strokeWidth={2.2} />
                      </div>
                      <div className="flex-1 pt-1">
                        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-white/55">
                          Step 0{i + 1}
                        </div>
                        <div className="font-display text-lg sm:text-xl font-bold mt-1">
                          {step.title}
                        </div>
                        <div className="mt-2 text-sm text-white/70 leading-relaxed max-w-md">
                          {step.body}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>

              <a
                href="#waitlist"
                data-testid="how-host-cta"
                className="group mt-12 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-carvny-ink font-bold text-sm hover:bg-carvny-red hover:text-white transition-colors"
              >
                Host Your Car
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>

          {/* Renters column — premium light */}
          <div
            data-testid="how-renters-column"
            className="relative bg-carvny-surface border border-carvny-border rounded-[28px] overflow-hidden flex flex-col"
          >
            {/* Image header */}
            <div className="relative h-52 sm:h-64 lg:h-72 overflow-hidden">
              <img
                src={SUPPORT_IMAGE}
                alt="Inside the driver seat of a luxury Mercedes-Benz at golden hour"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-carvny-surface via-carvny-surface/10 to-transparent" />
              <div className="absolute top-6 left-6 sm:top-8 sm:left-8">
                <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-carvny-red bg-white px-3 py-1.5 rounded-full border border-carvny-border">
                  For Renters
                </span>
              </div>
            </div>

            <div className="p-8 sm:p-12 flex-1">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-carvny-red">
                  Drive
                </span>
                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-carvny-muted">
                  Full transparency
                </span>
              </div>
              <div className="rule mt-5" />

              <h3 className="font-display mt-7 text-[34px] sm:text-[44px] font-black tracking-tight text-carvny-ink leading-[1.02]">
                The drive is yours.
                <span className="font-serif-italic font-normal text-carvny-ink/85"> The </span>
                <span className="text-carvny-red">guardrails are ours.</span>
              </h3>

              <ol className="mt-12 space-y-7">
                {RENTER_STEPS.map((step, i) => {
                  const Icon = step.icon;
                  return (
                    <li
                      key={step.title}
                      data-testid={`how-renter-step-${i + 1}`}
                      className="flex items-start gap-5"
                    >
                      <div className="shrink-0 w-12 h-12 rounded-2xl bg-carvny-red text-white flex items-center justify-center">
                        <Icon size={22} strokeWidth={2.2} />
                      </div>
                      <div className="flex-1 pt-1">
                        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-carvny-red">
                          Step 0{i + 1}
                        </div>
                        <div className="font-display text-lg sm:text-xl font-bold text-carvny-ink mt-1">
                          {step.title}
                        </div>
                        <div className="mt-2 text-sm text-carvny-muted leading-relaxed max-w-md">
                          {step.body}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>

              <a
                href="#waitlist"
                data-testid="how-renter-cta"
                className="group mt-12 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-carvny-red text-white font-bold text-sm hover:bg-carvny-red-hover transition-colors"
              >
                Rent a Car
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
